// Central design token system — 2 themes: navy (dark) + sky (light)

export interface AppTheme {
  id: string;
  name: string;
  mode: 'dark' | 'light';
  bodyGradient: string;
  accent: string;
  accentLight: string;
  accentGlow: string;
  glassBg: string;
  glassHover: string;
  glassBorder: string;
  glassBorderStrong: string;
  sidebarBg: string;
  sidebarBorder: string;
  selectedBg: string;
  selectedBorder: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  topbarBg: string;
  divider: string;
  inputBg: string;
  inputBorder: string;
  rowHover: string;
  rowSelected: string;
  // MUI overrides
  muiPrimary: string;
  muiBackground: string;
  muiPaper: string;
}

export const THEMES: AppTheme[] = [

  // Dark — Navy Blue
  {
    id: 'navy', name: 'Dark', mode: 'dark',
    bodyGradient: 'radial-gradient(ellipse 130% 80% at 15% 15%, #0c1f3f 0%, #060d1e 55%, #040c1a 100%)',
    accent: '#0284c7', accentLight: '#38bdf8', accentGlow: 'rgba(2,132,199,.4)',
    glassBg: 'rgba(10,18,42,.65)', glassHover: 'rgba(56,189,248,.07)', glassBorder: 'rgba(56,189,248,.1)', glassBorderStrong: 'rgba(56,189,248,.28)',
    sidebarBg: 'rgba(6,12,28,.82)', sidebarBorder: 'rgba(56,189,248,.08)',
    selectedBg: 'rgba(2,132,199,.18)', selectedBorder: '#0284c7',
    textPrimary: '#e2eaf7', textSecondary: '#7a8eb0', textDisabled: '#3d4f6e',
    topbarBg: 'rgba(6,12,28,.88)', divider: 'rgba(56,189,248,.07)',
    inputBg: 'rgba(56,189,248,.05)', inputBorder: 'rgba(56,189,248,.14)',
    rowHover: 'rgba(56,189,248,.04)', rowSelected: 'rgba(2,132,199,.13)',
    muiPrimary: '#0284c7', muiBackground: '#040c1a', muiPaper: '#0a1228',
  },

  // Light — Sky Blue
  {
    id: 'sky', name: 'Light', mode: 'light',
    bodyGradient: 'radial-gradient(ellipse 130% 80% at 15% 15%, #eff8ff 0%, #dbeefe 55%, #e8f4ff 100%)',
    accent: '#0284c7', accentLight: '#0369a1', accentGlow: 'rgba(2,132,199,.3)',
    // glassBg more opaque so cards/modals look white, not grey-blue over the body gradient
    glassBg: 'rgba(255,255,255,.92)', glassHover: 'rgba(2,132,199,.07)', glassBorder: 'rgba(2,132,199,.18)', glassBorderStrong: 'rgba(2,132,199,.4)',
    sidebarBg: 'rgba(255,255,255,.90)', sidebarBorder: 'rgba(2,132,199,.13)',
    selectedBg: 'rgba(2,132,199,.1)', selectedBorder: '#0284c7',
    textPrimary: '#04131f', textSecondary: '#2a5f80', textDisabled: '#7aadc6',
    topbarBg: 'rgba(255,255,255,.94)', divider: 'rgba(2,132,199,.15)',
    // Solid light-blue input bg so fields are clearly distinguishable from card background
    inputBg: 'rgba(235,248,255,1)', inputBorder: 'rgba(2,132,199,.3)',
    rowHover: 'rgba(2,132,199,.06)', rowSelected: 'rgba(2,132,199,.11)',
    muiPrimary: '#0284c7', muiBackground: '#bae6fd', muiPaper: '#ffffff',
  },
];

export const DEFAULT_THEME_ID = 'navy';

export const getTheme = (id: string): AppTheme =>
  THEMES.find(t => t.id === id) ?? THEMES[0];

export const applyThemeCssVars = (theme: AppTheme) => {
  const r = document.documentElement;
  r.style.setProperty('--accent',               theme.accent);
  r.style.setProperty('--accent-light',         theme.accentLight);
  r.style.setProperty('--accent-glow',          theme.accentGlow);
  r.style.setProperty('--glass-bg',             theme.glassBg);
  r.style.setProperty('--glass-hover',          theme.glassHover);
  r.style.setProperty('--glass-border',         theme.glassBorder);
  r.style.setProperty('--glass-border-strong',  theme.glassBorderStrong);
  r.style.setProperty('--sidebar-bg',           theme.sidebarBg);
  r.style.setProperty('--sidebar-border',       theme.sidebarBorder);
  r.style.setProperty('--selected-bg',          theme.selectedBg);
  r.style.setProperty('--selected-border',      theme.selectedBorder);
  r.style.setProperty('--text-primary',         theme.textPrimary);
  r.style.setProperty('--text-secondary',       theme.textSecondary);
  r.style.setProperty('--text-disabled',        theme.textDisabled);
  r.style.setProperty('--topbar-bg',            theme.topbarBg);
  r.style.setProperty('--divider',              theme.divider);
  r.style.setProperty('--input-bg',             theme.inputBg);
  r.style.setProperty('--input-border',         theme.inputBorder);
  r.style.setProperty('--row-hover',            theme.rowHover);
  r.style.setProperty('--row-selected',         theme.rowSelected);
  r.style.setProperty('--body-gradient',        theme.bodyGradient);
  r.style.setProperty('--theme-mode',           theme.mode);
  r.setAttribute('data-theme-mode', theme.mode);
  // Folder card gradient — light theme: pale tint; dark theme: rich saturated
  if (theme.mode === 'light') {
    r.style.setProperty('--folder-card-from', theme.accentLight + '55');
    r.style.setProperty('--folder-card-to',   theme.accent      + '88');
  } else {
    r.style.setProperty('--folder-card-from', theme.accent);
    r.style.setProperty('--folder-card-to',   theme.accentLight + 'bb');
  }
};
